import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { partiallyMaskValue } from '@dxb-web-ui-framework/common/utils/DataObfuscation';
import { Box, Button, Checkbox, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import { Loop } from '@dxb-web-ui-framework/design-system/dist/icons';
import { TableGridConfig } from '../../../../../content-composer/src/components/Generic/types';
import {
  formatAmountCellRenderer,
  stringWithPartiallyMaskValue,
  payrollDetailsStatusCellRenderer,
  vopStatusCellRenderer,
} from '../../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { useLanguage } from '../../../../../internationalization/src/contexts/Internationalization/hooks';
import { PayrollVopResult } from '../../../domain/Payroll/entities/types';
const useStyles = makeStyles(theme => ({
  typographyValue: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const usePayrollDetailsVopGridConfig = ({
  payrolls,
  selectedPayrolls,
  setSelectedPayrolls,
  onClickRetry,
}: {
  payrolls: PayrollVopResult[];
  selectedPayrolls: PayrollVopResult[];
  setSelectedPayrolls: (payrolls: PayrollVopResult[]) => void;
  onClickRetry: () => void;
}): TableGridConfig => {
  const language = useLanguage();
  const locale = language.cultureName;
  const { t } = useTranslation();
  const classes = useStyles();

  const onSelectPayrolls = useCallback(
    (payroll: PayrollVopResult) => {
      const shouldCheck = !selectedPayrolls.find(item => item.rowSn === payroll.rowSn);
      setSelectedPayrolls(
        shouldCheck ? [...selectedPayrolls, payroll] : selectedPayrolls.filter(item => item.rowSn !== payroll.rowSn),
      );
    },
    [setSelectedPayrolls, selectedPayrolls],
  );

  const isCheckBoxSelected = useCallback(
    (payroll: { rowSn: number }): boolean => {
      const isPayrollFound = selectedPayrolls?.find(item => item.rowSn === payroll.rowSn);
      return Boolean(isPayrollFound);
    },
    [selectedPayrolls],
  );

  const onToggleSelectAllPendingVop = useCallback(() => {
    const pending = payrolls ? payrolls.filter(p => p.vopStatus === 'NOT_COMPLETED') : [];

    const allPendingSelected = pending.every(p => selectedPayrolls.some(s => s.rowSn === p.rowSn));

    if (allPendingSelected) {
      const pendingIds = new Set(pending.map(p => p.rowSn));
      setSelectedPayrolls(selectedPayrolls.filter(item => !pendingIds.has(item.rowSn)));
    } else {
      const prevIds = new Set(selectedPayrolls.map(p => p.rowSn));
      const newItems = pending.filter(p => !prevIds.has(p.rowSn));
      setSelectedPayrolls([...selectedPayrolls, ...newItems]);
    }
  }, [selectedPayrolls, payrolls]);

  const isCheckBoxAllSelected = useCallback((): boolean => {
    const pending = payrolls ? payrolls.filter(p => p.vopStatus === 'NOT_COMPLETED') : [];

    if (pending.length === 0) return false;

    return pending.every(p => selectedPayrolls.some(s => s.rowSn === p.rowSn));
  }, [selectedPayrolls, payrolls]);

  return {
    gridOptions: {
      ariaTitle: t('usePayrollDetailsGridConfig.GridOptions.AriaTitle'),
      dataTestId: 'payroll-details-table',
      noResultsMsg: t('usePayrollDetailsGridConfig.NoResultMessage'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'Row Sn',
        field: 'rowSn',
        label: t('usePayrollDetailsGridConfig.Label.RowSn'),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Account credit',
        field: 'iban',
        label: t('usePayrollDetailsGridConfig.Label.AccountCredit'),
        cellRenderer: stringWithPartiallyMaskValue(6, 18),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Amount',
        field: 'amount',
        label: t('usePayrollDetailsGridConfig.Label.Amount'),
        cellRenderer: formatAmountCellRenderer(locale, false),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Employee name',
        field: 'beneficiaryName',
        label: t('usePayrollDetailsGridConfig.Label.EmployeeName'),
        columnHeaderCellProps: { align: 'right', colSpan: 1 },
        cellProps: { align: 'right', colSpan: 1 },
      },
      {
        id: 'Vop Status',
        field: 'vopStatus',
        label: t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.HelperText.Text'),
        cellRenderer: vopStatusCellRenderer,
        columnHeaderCellProps: { colSpan: 1 },
        cellProps: { colSpan: 1 },
      },
      {
        id: 'SelectVop',
        field: '',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Check'),
        cellRenderer: (rowData: any) => (
          <Box display={'flex'} justifyContent={'end'}>
            {rowData.vopStatus === 'NOT_COMPLETED' && (
              <Checkbox
                key={rowData.rowSn}
                id={rowData.rowSn}
                size='small'
                checked={isCheckBoxSelected(rowData)}
                onChange={() => onSelectPayrolls(rowData)}
              />
            )}
          </Box>
        ),
        columnHeaderRenderer: () => (
          <Box display={'flex'} alignItems={'center'} justifyContent={'end'}>
            Select All
            <Checkbox size='small' checked={isCheckBoxAllSelected()} onChange={onToggleSelectAllPendingVop} />
          </Box>
        ),
      },
      {
        id: 'RetryVop',
        field: '',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Check'),
        cellRenderer: (rowData: any) => <></>,
        columnHeaderRenderer: () => (
          <Box display={'flex'} alignItems={'center'} justifyContent={'start'}>
            <Button
              color='primary'
              isIconButton
              size='small'
              variant='contained'
              onClick={onClickRetry}
              disabled={selectedPayrolls.length === 0}
            >
              <Loop />
            </Button>
          </Box>
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'beneficiaryName',
          value: 'iban',
          valueAreaLabel: 'iban',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: any) => (
            <Typography
              color='textSecondary'
              component='p'
              aria-label={String(partiallyMaskValue(data.iban, 6, 18))}
              className={classes.typographyValue}
            >
              {partiallyMaskValue(data.iban, 6, 18)}
            </Typography>
          ),
        },
        {
          label: 'amount',
          value: 'amount',
          valueAreaLabel: 'amount',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelAlignment: 'right',
          valueTransformer: (data: any) => (
            <Box mt={3} display='flex' component='span' justifyContent={'flex-end'}>
              {payrollDetailsStatusCellRenderer(data, { field: 'vopStatus' })}
            </Box>
          ),
        },
      ],
    },
  };
};

export { usePayrollDetailsVopGridConfig };
